import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import { isSupabaseConfigured, supabase } from './supabase';
import { getDeclaredAuthMode, isPreviewReviewAllowed, isStubModeAllowed } from './env';
import type { AuthIdentity, AutoProfile, CampusRole, RoboTeamAccess } from './types';
import { getImpersonationToken, clearImpersonation } from './impersonation';
import { hasAnyPermission, hasPermission, type Permission } from './permissions';

/** Roles válidos del enum `campus.users.role` (espejo de VALID_ROLES backend).
 *  Cualquier valor desconocido del backend se normaliza a 'student'
 *  (deny-by-default · nunca conceder admin por un valor inesperado). */
const VALID_ROLES: readonly CampusRole[] = [
  'owner',
  'admin',
  'soporte',
  'tutor',
  'staff',
  'student',
];

function normalizeRole(raw: unknown): CampusRole {
  return typeof raw === 'string' && (VALID_ROLES as readonly string[]).includes(raw)
    ? (raw as CampusRole)
    : 'student';
}

/** Extrae el objeto `auto` de la respuesta de `GET /api/me/profile`.
 *  Devuelve el objeto si viene, null en cualquier otro caso (campo ausente,
 *  payload nulo/undefined). Helper puro, sin efectos secundarios. */
export function extractAuto(profileResponse: unknown): AutoProfile | null {
  if (profileResponse == null || typeof profileResponse !== 'object') return null;
  const auto = (profileResponse as Record<string, unknown>).auto;
  return auto != null ? (auto as AutoProfile) : null;
}

/** Extrae el objeto `roboteam` de la respuesta de `GET /api/me/profile`.
 *  Devuelve null si el campo no viene o no tiene la forma esperada. */
export function extractRoboTeam(profileResponse: unknown): RoboTeamAccess | null {
  if (profileResponse == null || typeof profileResponse !== 'object') return null;
  const rt = (profileResponse as Record<string, unknown>).roboteam;
  if (rt == null || typeof rt !== 'object') return null;
  const member = (rt as Record<string, unknown>).member === true;
  const rawTier = (rt as Record<string, unknown>).tier;
  const tier = rawTier === 'pro' || rawTier === 'basic' ? rawTier : null;
  return { member, tier };
}

/** Datos de autorización del perfil · role real (6 roles) + permisos + estado
 *  de impersonación + objeto auto (opcional). Reemplaza al antiguo `fetchUserRole` binario. */
export interface ProfileAuth {
  role: CampusRole | null;
  permissions: string[];
  impersonating: boolean;
  impersonated_by: string | null;
  auto: AutoProfile | null;
  roboteam: RoboTeamAccess | null;
  /** Email/nombre del usuario EFECTIVO según el backend (`/api/me/profile`).
   *  Bajo impersonación son los del ALUMNO objetivo — se usan para que la
   *  identidad visible (saludo, perfil del sidebar) refleje al alumno y no al
   *  admin de la sesión Supabase (F-CAMPUS-IMPERSONATION-IDENTITY-001). */
  profile_email: string | null;
  profile_name: string | null;
}

const EMPTY_PROFILE_AUTH: ProfileAuth = {
  role: null,
  permissions: [],
  impersonating: false,
  impersonated_by: null,
  auto: null,
  roboteam: null,
  profile_email: null,
  profile_name: null,
};

// Clave de persistencia de la sesión de REVISIÓN solo-preview. Permite que la
// sesión sobreviva a la navegación entre páginas sin re-pulsar el botón. Se
// re-valida con isPreviewReviewAllowed() en CADA carga: si el bundle se sirviera
// desde un host de prod, la rehidratación se ignora (el gate por host manda).
const REVIEW_SESSION_KEY = 'robotea-review-session';
const REVIEW_EMAIL = 'revision@preview.robotea.io';
const REVIEW_NAME = 'Revisión (preview)';

/** Construye la identidad de revisión (mock, sin JWT, mode='review'). */
function reviewIdentity(): AuthIdentity {
  return {
    email: REVIEW_EMAIL,
    name: REVIEW_NAME,
    mode: 'review',
    jwt: null,
    is_stub: true,
    // Rol 'admin' + permisos completos SOLO cosmético: deja ver TODAS las
    // secciones del shell (incl. el panel admin) en la preview. Sin JWT, el
    // backend nunca trata esta sesión como admin; las llamadas /api/* salen sin
    // credenciales y caen a vacío. (Permisos amplios pero inertes server-side.)
    role: 'admin',
    permissions: [
      'admin.analitica.view',
      'admin.usuarios.view',
      'admin.usuarios.actions',
      'admin.entitlements.write',
      'admin.contenido.write',
      'admin.licencias.view',
      'admin.licencias.approve',
      'admin.actividad.view',
      'admin.integraciones',
      'admin.export_pii',
      'admin.impersonate',
      'campus.access_as_student',
    ],
    impersonating: false,
    impersonated_by: null,
    auto: null,
    roboteam: null,
  };
}

/**
 * Fetchea el perfil del usuario y extrae los datos de autorización: rol REAL
 * (6 roles), `permissions[]` y estado de impersonación. El backend
 * (`/api/me/profile`) es la fuente de la verdad de los permisos del rol
 * efectivo; bajo impersonación devuelve el rol/permisos del ALUMNO objetivo y
 * `impersonating: true` con `impersonated_by` = email del admin real.
 *
 * Si hay un token de impersonación activo en el front lo usa como Bearer
 * (preferente sobre el JWT real) para que el perfil refleje al alumno.
 */
async function fetchProfileAuth(jwt: string | null): Promise<ProfileAuth> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const impToken = getImpersonationToken();
    if (impToken) {
      headers['Authorization'] = `Bearer ${impToken}`;
    } else if (jwt) {
      headers['Authorization'] = `Bearer ${jwt}`;
    }
    const res = await fetch('/api/me/profile', { headers });
    if (!res.ok) {
      // Token de impersonación caducado/ inválido → limpiarlo para no quedar
      // pegados a una vista de alumno rota.
      if (impToken && (res.status === 401 || res.status === 403)) {
        clearImpersonation();
      }
      // eslint-disable-next-line no-console
      console.warn('[auth] fetchProfileAuth failed:', res.status, await res.text().catch(() => ''));
      return EMPTY_PROFILE_AUTH;
    }
    const data = (await res.json()) as {
      role?: unknown;
      permissions?: unknown;
      impersonating?: unknown;
      impersonated_by?: unknown;
      auto?: unknown;
      email?: unknown;
      full_name?: unknown;
    };
    const permissions = Array.isArray(data.permissions)
      ? data.permissions.filter((p): p is string => typeof p === 'string')
      : [];
    return {
      role: normalizeRole(data.role),
      permissions,
      impersonating: data.impersonating === true,
      impersonated_by:
        typeof data.impersonated_by === 'string' ? data.impersonated_by : null,
      auto: extractAuto(data),
      roboteam: extractRoboTeam(data),
      profile_email: typeof data.email === 'string' ? data.email : null,
      profile_name: typeof data.full_name === 'string' ? data.full_name : null,
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[auth] fetchProfileAuth error:', err);
    return EMPTY_PROFILE_AUTH;
  }
}

/** Email/nombre VISIBLES de la identidad. Bajo impersonación la identidad
 *  visible es la del ALUMNO objetivo (viene de `/api/me/profile`, que ya
 *  resuelve por el token de impersonación); en sesión normal, la de la sesión
 *  real (Supabase/stub). Solo afecta a lo que se MUESTRA (saludo, sidebar);
 *  el JWT y el mode de la sesión real no se tocan. */
function visibleIdentityFields(
  auth: ProfileAuth,
  sessionEmail: string,
  sessionName: string | null,
): { email: string; name: string | null } {
  if (auth.impersonating) {
    return {
      email: auth.profile_email ?? sessionEmail,
      name: auth.profile_name ?? auth.profile_email ?? null,
    };
  }
  return { email: sessionEmail, name: sessionName };
}

type AuthState =
  | { status: 'loading'; identity: null }
  | { status: 'unauthenticated'; identity: null }
  | { status: 'authenticated'; identity: AuthIdentity };

type AuthAction =
  | { type: 'set_loading' }
  | { type: 'set_unauthenticated' }
  | { type: 'set_authenticated'; identity: AuthIdentity };

function reducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'set_loading':
      return { status: 'loading', identity: null };
    case 'set_unauthenticated':
      return { status: 'unauthenticated', identity: null };
    case 'set_authenticated':
      return { status: 'authenticated', identity: action.identity };
    default:
      return state;
  }
}

interface AuthContextValue {
  state: AuthState;
  signOut: () => Promise<void>;
  setStubIdentity: (email: string) => Promise<void>;
  /** Activa la sesión de REVISIÓN solo-preview. No-op (con warn) si el host no
   *  es una preview de Vercel. */
  enterReviewMode: () => void;
  /** Re-resuelve el perfil (rol + permisos + estado de impersonación) de la
   *  sesión REAL actual sin re-loguear. Usado tras iniciar/terminar una
   *  impersonación para que la identidad refleje al alumno (o vuelva al admin).
   *  Si hay token de impersonación activo, `/api/me/profile` se pide con él. */
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Strip any ?stub_email=… query param from the current URL. Used both
 *  cuando el usuario hace signOut y, defensivamente, cuando un build
 *  production detecta un stub_email en la URL (debe ser ignorado y
 *  purgado para que el link no sea visible/compartible). */
function scrubStubEmailFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (url.searchParams.has('stub_email')) {
    url.searchParams.delete('stub_email');
    window.history.replaceState({}, '', url.toString());
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, dispatch] = useReducer(reducer, {
    status: 'loading',
    identity: null,
  });

  // Ref siempre apuntando al último `state`, para que callbacks estables
  // (refreshAuth) lean el estado actual sin recrearse en cada render.
  const stateRef = useRef(state);
  stateRef.current = state;

  const declaredMode = getDeclaredAuthMode();
  const stubAllowed = isStubModeAllowed();

  useEffect(() => {
    let active = true;

    async function init(): Promise<void> {
      // 0) Sesión de REVISIÓN gateada por host. Si el gate lo permite
      //    (*.vercel.app o app2.* · nunca dominio de prod) Y hay una sesión de revisión
      //    persistida, rehidratamos la identidad mock para que sobreviva a la
      //    navegación. El gate se re-evalúa AQUÍ en cada carga: si el mismo
      //    bundle se sirviera desde prod, isPreviewReviewAllowed() es false y
      //    esta rama no se ejecuta (defensa por host). Si el gate ya no aplica,
      //    purgamos cualquier flag residual.
      if (isPreviewReviewAllowed()) {
        let hasReview = false;
        try {
          hasReview = localStorage.getItem(REVIEW_SESSION_KEY) === '1';
        } catch {
          /* storage no disponible */
        }
        if (hasReview) {
          if (!active) return;
          dispatch({ type: 'set_authenticated', identity: reviewIdentity() });
          return;
        }
      } else {
        // Host no-preview: nunca debe quedar una sesión de revisión activa.
        try {
          localStorage.removeItem(REVIEW_SESSION_KEY);
        } catch {
          /* storage no disponible */
        }
      }

      // 1) URL ?stub_email=… short-circuit (modo dev sin auth real).
      //    Gated por triple condición: import.meta.env.DEV === true
      //    AND VITE_AUTH_MODE === 'stub' (centralizado en isStubModeAllowed()).
      //    En producción cualquier ?stub_email= se ignora silenciosamente,
      //    se loggea un warn y se purga de la URL para que no quede
      //    compartible (defense in depth · F-AUDIT-MVP-001).
      const params = new URLSearchParams(window.location.search);
      const stubEmail = params.get('stub_email');
      if (stubEmail) {
        if (stubAllowed) {
          if (!active) return;
          const auth = await fetchProfileAuth(null);
          dispatch({
            type: 'set_authenticated',
            identity: {
              email: stubEmail,
              name: null,
              mode: 'stub',
              jwt: null,
              is_stub: true,
              role: auth.role,
              permissions: auth.permissions,
              impersonating: auth.impersonating,
              impersonated_by: auth.impersonated_by,
              auto: null,
              roboteam: auth.roboteam,
            },
          });
          return;
        }
        // eslint-disable-next-line no-console
        console.warn(
          '[auth] stub_email ignored: requires dev build (import.meta.env.DEV) AND VITE_AUTH_MODE=stub',
        );
        scrubStubEmailFromUrl();
        // continúa al flujo normal abajo (no autenticar como stub)
      }

      // 2) Supabase no configurado o modo stub explícito → unauthenticated
      //    (UI mostrará banner / login).
      if (declaredMode === 'stub' || !isSupabaseConfigured() || !supabase) {
        if (!active) return;
        dispatch({ type: 'set_unauthenticated' });
        return;
      }

      // 3) Resolver sesión Supabase existente
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      if (data.session) {
        const auth = await fetchProfileAuth(data.session.access_token);
        dispatch({
          type: 'set_authenticated',
          identity: {
            ...visibleIdentityFields(
              auth,
              data.session.user.email ?? '',
              (data.session.user.user_metadata?.full_name as string | undefined) ?? null,
            ),
            mode: 'supabase',
            jwt: data.session.access_token,
            is_stub: false,
            role: auth.role,
            permissions: auth.permissions,
            impersonating: auth.impersonating,
            impersonated_by: auth.impersonated_by,
            auto: auth.auto,
            roboteam: auth.roboteam,
          },
        });
      } else {
        dispatch({ type: 'set_unauthenticated' });
      }
    }

    init().catch(() => {
      if (active) dispatch({ type: 'set_unauthenticated' });
    });

    // Listener cambios sesión Supabase
    if (declaredMode === 'supabase' && isSupabaseConfigured() && supabase) {
      const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
        if (!active) return;
        if (session) {
          // No await en el callback de onAuthStateChange (Supabase docs recommendation)
          fetchProfileAuth(session.access_token).then((auth) => {
            if (!active) return;
            dispatch({
              type: 'set_authenticated',
              identity: {
                ...visibleIdentityFields(
                  auth,
                  session.user.email ?? '',
                  (session.user.user_metadata?.full_name as string | undefined) ?? null,
                ),
                mode: 'supabase',
                jwt: session.access_token,
                is_stub: false,
                role: auth.role,
                permissions: auth.permissions,
                impersonating: auth.impersonating,
                impersonated_by: auth.impersonated_by,
                auto: auth.auto,
                roboteam: auth.roboteam,
              },
            });
          });
        } else {
          dispatch({ type: 'set_unauthenticated' });
        }
      });
      return () => {
        active = false;
        sub.subscription.unsubscribe();
      };
    }

    return () => {
      active = false;
    };
  }, [declaredMode, stubAllowed]);

  const signOut = useCallback(async (): Promise<void> => {
    if (isSupabaseConfigured() && supabase) {
      await supabase.auth.signOut();
    }
    scrubStubEmailFromUrl();
    // Limpia también cualquier sesión de revisión solo-preview.
    try {
      localStorage.removeItem(REVIEW_SESSION_KEY);
    } catch {
      /* storage no disponible */
    }
    // Defensa: nunca dejar un token de impersonación colgando al cerrar sesión.
    clearImpersonation();
    dispatch({ type: 'set_unauthenticated' });
  }, []);

  // Re-resuelve rol/permisos/impersonación de la sesión actual. Reusa el JWT
  // real de Supabase (o ninguno en modo stub/review). Bajo impersonación
  // fetchProfileAuth usa el token de impersonación como Bearer → el perfil
  // refleja al alumno, y la identidad VISIBLE (email/name) pasa a ser la del
  // alumno objetivo; al terminar la impersonación se restaura la de la sesión
  // real (Supabase). mode/jwt nunca cambian. No-op si no hay sesión autenticada.
  const refreshAuth = useCallback(async (): Promise<void> => {
    const current = stateRef.current;
    if (current.status !== 'authenticated') return;
    const identity = current.identity;
    // Sesión de revisión (mock): no hay backend que consultar; mantenerla tal cual.
    if (identity.mode === 'review') return;
    const auth = await fetchProfileAuth(identity.jwt);
    // Base de la identidad visible = la sesión REAL. identity.email/name pueden
    // llevar los del alumno impersonado, así que se re-leen de Supabase (si
    // aplica) para que al PARAR la impersonación vuelva el admin real.
    let sessionEmail = identity.email;
    let sessionName = identity.name;
    if (identity.mode === 'supabase' && isSupabaseConfigured() && supabase) {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        sessionEmail = data.session.user.email ?? sessionEmail;
        sessionName =
          (data.session.user.user_metadata?.full_name as string | undefined) ?? null;
      }
    }
    dispatch({
      type: 'set_authenticated',
      identity: {
        ...identity,
        ...visibleIdentityFields(auth, sessionEmail, sessionName),
        role: auth.role,
        permissions: auth.permissions,
        impersonating: auth.impersonating,
        impersonated_by: auth.impersonated_by,
        auto: auth.auto,
        roboteam: auth.roboteam,
      },
    });
  }, []);

  const enterReviewMode = useCallback((): void => {
    // Gate por host: SOLO en previews de Vercel (*.vercel.app, no dominio prod).
    // Si alguien llama esto fuera de una preview (consola, etc.) se ignora.
    if (!isPreviewReviewAllowed()) {
      // eslint-disable-next-line no-console
      console.warn(
        '[auth] enterReviewMode ignored: requires Vercel preview host (*.vercel.app)',
      );
      return;
    }
    try {
      localStorage.setItem(REVIEW_SESSION_KEY, '1');
    } catch {
      /* storage no disponible · la sesión vivirá solo en memoria */
    }
    dispatch({ type: 'set_authenticated', identity: reviewIdentity() });
  }, []);

  const setStubIdentity = useCallback(async (email: string): Promise<void> => {
    // Refuse to set a stub identity outside dev+stub mode, aunque algún
    // caller (test, story) intente saltarse el gate de la URL.
    if (!isStubModeAllowed()) {
      // eslint-disable-next-line no-console
      console.warn(
        '[auth] setStubIdentity ignored: requires dev build (import.meta.env.DEV) AND VITE_AUTH_MODE=stub',
      );
      return;
    }
    const auth = await fetchProfileAuth(null);
    dispatch({
      type: 'set_authenticated',
      identity: {
        email,
        name: null,
        mode: 'stub',
        jwt: null,
        is_stub: true,
        role: auth.role,
        permissions: auth.permissions,
        impersonating: auth.impersonating,
        impersonated_by: auth.impersonated_by,
        auto: null,
        roboteam: auth.roboteam,
      },
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, signOut, setStubIdentity, enterReviewMode, refreshAuth }),
    [state, signOut, setStubIdentity, enterReviewMode, refreshAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }
  return ctx;
}

/**
 * Helpers de permisos basados en la identidad autenticada actual. Devuelve un
 * predicado `can(perm)` (deny-by-default sobre `identity.permissions`, que vino
 * del backend) + el array crudo de permisos. Para usuarios no autenticados,
 * `can` siempre devuelve false. La decisión de seguridad real es server-side;
 * esto solo decide qué se MUESTRA.
 */
export function usePermissions(): {
  permissions: string[];
  can: (perm: Permission) => boolean;
  canAny: (perms: readonly Permission[]) => boolean;
} {
  const { state } = useAuth();
  const permissions = state.status === 'authenticated' ? state.identity.permissions : [];
  const can = useCallback(
    (perm: Permission): boolean => hasPermission(permissions, perm),
    [permissions],
  );
  const canAny = useCallback(
    (perms: readonly Permission[]): boolean => hasAnyPermission(permissions, perms),
    [permissions],
  );
  return { permissions, can, canAny };
}
